// ═══════════════════════════════════════════════════════════════════════════
// CORRISPONDENZA CON IL CLIENTE DURANTE L'ESECUZIONE (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Decisione di Paolo: quello che il cliente segnala MENTRE la commessa è in esecuzione si
// registra in Corrispondenza della commessa (non in Segnalazioni Cliente, che è per
// l'assistenza dopo la consegna: guasti, problemi dell'impianto, richieste d'intervento).
// Ogni comunicazione può indicare se è stata ricevuta o inviata e, se è con il Cliente, di
// che tipo è; una segnalazione o un reclamo resta «da gestire» finché non si indica come
// è stato risolto. La Relazione della commessa e la Cabina di Regia leggono questi dati.
// Le comunicazioni registrate prima restano valide: senza tipo contano come generiche.
// ═══════════════════════════════════════════════════════════════════════════

export const DIREZIONI = { ricevuta: "Ricevuta", inviata: "Inviata" };

export const TIPI_CLIENTE = {
  generica:     { id: "generica",     label: "Comunicazione generica" },
  segnalazione: { id: "segnalazione", label: "Segnalazione o reclamo" },
  variante:     { id: "variante",     label: "Richiesta di variante" },
  approvazione: { id: "approvazione", label: "Approvazione / accettazione" },
  sollecito:    { id: "sollecito",    label: "Sollecito" },
};

export const eCliente = (c) => (c?.categoria || "") === "Cliente";
/** Tipo indicato (null se la comunicazione non è con il Cliente o se il tipo non è stato indicato). */
export const tipoCliente = (c) => (eCliente(c) && TIPI_CLIENTE[c?.tipoCliente] ? c.tipoCliente : null);
/** Comunicazione con il Cliente registrata senza tipo (es. prima di ottobre 2026): non si sa se è una segnalazione. */
export const senzaTipo = (c) => eCliente(c) && !TIPI_CLIENTE[c?.tipoCliente];
export const eSegnalazione = (c) => tipoCliente(c) === "segnalazione";
export const segnalazioneAperta = (c) => eSegnalazione(c) && c?.statoSegnalazione !== "gestita";

/** Riepilogo dei rapporti con il Cliente per Relazione e Cabina di Regia. */
export function riepilogoCorrispondenzaCliente(comunicazioni = []) {
  const cli = comunicazioni.filter(eCliente);
  const r = { totale: cli.length, elenco: [...cli].sort((a, b) => String(a.dataComunicazione || "").localeCompare(String(b.dataComunicazione || ""))),
    ricevute: 0, inviate: 0, segnalazioni: [], aperte: [], gestite: [], senzaTipo: [], varianti: 0, approvazioni: 0, solleciti: 0 };
  cli.forEach(c => {
    if (c.direzione === "ricevuta") r.ricevute++;
    if (c.direzione === "inviata") r.inviate++;
    const t = tipoCliente(c);
    if (!t) r.senzaTipo.push(c);
    if (t === "segnalazione") { r.segnalazioni.push(c); (segnalazioneAperta(c) ? r.aperte : r.gestite).push(c); }
    if (t === "variante") r.varianti++;
    if (t === "approvazione") r.approvazioni++;
    if (t === "sollecito") r.solleciti++;
  });
  return r;
}

const dataIt = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("it-IT") : "");

/** Una comunicazione in una riga: «02/10/2026 — ricevuta — «Oggetto» (segnalazione, gestita il …: …)». */
export function rigaComunicazione(c) {
  const t = tipoCliente(c);
  const dett = !t ? "tipo non indicato"
    : t === "segnalazione" ? `segnalazione, ${segnalazioneAperta(c) ? "da gestire" : `gestita${c.gestitaIl ? ` il ${dataIt(c.gestitaIl)}` : ""}${c.risoluzione ? `: ${c.risoluzione}` : ""}`}`
    : TIPI_CLIENTE[t].label.toLowerCase();
  return [dataIt(c.dataComunicazione), c.direzione ? DIREZIONI[c.direzione].toLowerCase() : "", `«${c.oggetto || "senza oggetto"}»`].filter(Boolean).join(" — ") + ` (${dett})`;
}

/** Testo del paragrafo «Rapporti con il Cliente durante l'esecuzione». */
export function testoRapportiCliente(r, { aperta = true } = {}) {
  if (!r.totale) return "Nella Corrispondenza della commessa non risultano comunicazioni con il Cliente né segnalazioni da parte sua.";
  const parti = [];
  const dir = [r.ricevute ? `${r.ricevute} ricevut${r.ricevute === 1 ? "a" : "e"}` : "", r.inviate ? `${r.inviate} inviat${r.inviate === 1 ? "a" : "e"}` : ""].filter(Boolean).join(" e ");
  parti.push(`Registrate in Corrispondenza ${r.totale} comunicazion${r.totale === 1 ? "e" : "i"} con il Cliente${dir ? ` (${dir})` : ""}${r.totale <= 5 ? ": " + r.elenco.map(rigaComunicazione).join("; ") + "." : "."}`);
  if (r.totale > 5) {
    // molte comunicazioni: riepilogo con i numeri, e in chiaro solo segnalazioni e quelle senza tipo
    if (r.segnalazioni.length) parti.push(`Segnalazioni o reclami del Cliente: ${r.segnalazioni.length}${r.gestite.length ? `, di cui ${r.gestite.length} gestit${r.gestite.length === 1 ? "a" : "e"}` : ""}: ${r.segnalazioni.map(rigaComunicazione).join("; ")}.`);
    if (r.varianti) parti.push(`Richieste di variante: ${r.varianti}.`);
    if (r.approvazioni) parti.push(`Approvazioni o accettazioni del Cliente: ${r.approvazioni}.`);
    if (r.solleciti) parti.push(`Solleciti: ${r.solleciti}.`);
    if (r.senzaTipo.length) parti.push(`Senza tipo indicato: ${r.senzaTipo.map(rigaComunicazione).join("; ")}.`);
  }
  // Conclusione sulle segnalazioni: si afferma che non ce ne sono solo se tutte le comunicazioni hanno il tipo
  if (r.segnalazioni.length) parti.push(r.aperte.length ? `${r.aperte.length === 1 ? "1 segnalazione è" : `${r.aperte.length} segnalazioni sono`} ancora da gestire.` : "Tutte le segnalazioni del Cliente risultano gestite.");
  else if (!r.senzaTipo.length) parti.push(`${aperta ? "Finora non risultano" : "Durante l'esecuzione non risultano"} segnalazioni o reclami da parte del Cliente.`);
  if (r.senzaTipo.length) parti.push(`${r.senzaTipo.length === 1 ? "Per 1 comunicazione" : `Per ${r.senzaTipo.length} comunicazioni`} non è indicato il tipo: verificare se si tratta di segnalazioni del Cliente.`);
  return parti.join(" ");
}
